import { Dispatch, SetStateAction, VFC } from 'react'
import { MasterDataStore } from 'src/api/masterData'
import { CheckboxDiv } from 'src/elements/Checkbox'
import {
  MasterData,
  MasterDataWithRegionalDivisionType,
} from 'src/models/MasterData'
import { Persona } from 'src/models/Persona'
import { ROLE, RoleType } from 'src/models/User'
import {
  _profilehrborder,
  bordergray,
  mediumGray,
  orange,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { categorizePrefecturesByRegion } from 'src/utils/categorizePrefecture'
import styled, { css } from 'styled-components'
import { SelectableItemDiv, SelectableItems } from '../formComponents'
import {
  NestedChecklabelHeader,
  PersonaInputDateForm,
  PersonaInputForm,
  PersonaNeedsInputForm,
  PersonaSolutionsInputForm,
  PrefectureInputForm,
  Types,
} from './form'

const types: Types[] = [
  'businessTypes',
  'numberOfEmployees',
  'needs',
  'solutions',
  'prefectures',
  'positions',
  'capitals',
  'others',
]

type PersonaBoxComponentProps = {
  pageType: 'persona' | 'need'
  role: RoleType | ''
  selectedType: Types
  setSelectedType: Dispatch<SetStateAction<Types>>
  checkedBusinessTypes: MasterData[]
  setCheckedBusinessTypes: Dispatch<SetStateAction<MasterData[]>>
  checkedNumberOfEmployees: MasterData[]
  setCheckedNumberOfEmployees: Dispatch<SetStateAction<MasterData[]>>
  checkedNeeds: MasterData[]
  setCheckedNeeds: Dispatch<SetStateAction<MasterData[]>>
  checkedSolutions: MasterData[]
  setCheckedSolutions: Dispatch<SetStateAction<MasterData[]>>
  checkedPrefectures: MasterDataWithRegionalDivisionType[]
  setCheckedPrefectures: Dispatch<
    SetStateAction<MasterDataWithRegionalDivisionType[]>
  >
  checkedPositionNumberOptions: MasterData[]
  setCheckedPositionNumberOptions: Dispatch<SetStateAction<MasterData[]>>
  checkedCapitalNumberOptions: MasterData[]
  setCheckedCapitalNumberOptions: Dispatch<SetStateAction<MasterData[]>>
  checkedFoundedAts: MasterData[]
  setCheckedFoundedAts: Dispatch<SetStateAction<MasterData[]>>
  checkedBusinessObjects: MasterData[]
  setCheckedBusinessObject: Dispatch<SetStateAction<MasterData[]>>
  checkedAdminAges: MasterData[]
  setCheckedAdminAges: Dispatch<SetStateAction<MasterData[]>>
}

type SearchForReferrerProps = {
  minCreatedDate?: string
  setMinCreatedDate?: Dispatch<SetStateAction<string>>
  maxCreatedDate?: string
  setMaxCreatedDate?: Dispatch<SetStateAction<string>>
  minLastSignInAt?: string
  setMinLastSignInAt?: Dispatch<SetStateAction<string>>
  maxLastSignInAt?: string
  setMaxLastSignInAt?: Dispatch<SetStateAction<string>>
  masterDataRoleForAnalysis?: MasterData[]
  checkedRoleForAnalysis?: MasterData[]
  setCheckedRoleForAnalysis?: Dispatch<SetStateAction<MasterData[]>>
}

type SearchDetailProps = {
  persona: Persona
  setPersona: Dispatch<Persona>
  handleOtherDetailsChecked?: boolean
  setOtherDetails?: (checked: boolean) => void
}

type SearchOtherProps = {
  persona: Persona
  setPersona: Dispatch<Persona>
  masterData: MasterDataStore
  checkedFoundedAts: MasterData[]
  setCheckedFoundedAts: Dispatch<SetStateAction<MasterData[]>>
  checkedBusinessObjects: MasterData[]
  setCheckedBusinessObject: Dispatch<SetStateAction<MasterData[]>>
  checkedAdminAges: MasterData[]
  setCheckedAdminAges: Dispatch<SetStateAction<MasterData[]>>
}

export const displayType = (type: Types) => {
  switch (type) {
    case 'businessTypes':
      return '業種'
    case 'numberOfEmployees':
      return '従業員数'
    case 'needs':
      return 'ニーズ'
    case 'solutions':
      return 'ソリューション'
    case 'prefectures':
      return '所在地'
    case 'positions':
      return '役職'
    case 'capitals':
      return '資本金'
    case 'others':
      return 'その他'
  }
}
export const PersonaTypes: VFC<{
  pageType: 'persona' | 'need'
  selectedType: Types
  setSelectedType: (type: Types) => void
}> = ({ pageType, selectedType, setSelectedType }) => {
  return (
    <PersonaNav>
      <PersonaMenu>
        {types.map((type) => {
          if (pageType === 'persona' && type === 'solutions') return null
          return (
            <PersonaMenuList
              open={selectedType === type}
              key={type}
              onClick={() => setSelectedType(type)}
            >
              <PersonaMenuListText>{displayType(type)}</PersonaMenuListText>
            </PersonaMenuList>
          )
        })}
      </PersonaMenu>
    </PersonaNav>
  )
}

export const PersonaBoxComponent: VFC<
  PersonaBoxComponentProps &
    SearchForReferrerProps &
    SearchDetailProps &
    SearchOtherProps
> = ({
  pageType,
  role,
  masterData,
  selectedType,
  setSelectedType,
  checkedBusinessTypes,
  setCheckedBusinessTypes,
  checkedNumberOfEmployees,
  setCheckedNumberOfEmployees,
  checkedNeeds,
  setCheckedNeeds,
  checkedSolutions,
  setCheckedSolutions,
  checkedPrefectures,
  setCheckedPrefectures,
  checkedPositionNumberOptions,
  setCheckedPositionNumberOptions,
  checkedCapitalNumberOptions,
  setCheckedCapitalNumberOptions,
  handleOtherDetailsChecked,
  setOtherDetails,
  persona,
  setPersona,
  checkedFoundedAts,
  setCheckedFoundedAts,
  checkedBusinessObjects,
  setCheckedBusinessObject,
  checkedAdminAges,
  setCheckedAdminAges,
  minCreatedDate,
  setMinCreatedDate,
  maxCreatedDate,
  setMaxCreatedDate,
  minLastSignInAt,
  setMinLastSignInAt,
  maxLastSignInAt,
  setMaxLastSignInAt,
  masterDataRoleForAnalysis,
  checkedRoleForAnalysis,
  setCheckedRoleForAnalysis,
}) => {
  return (
    <>
      <PersonaTypes
        pageType={pageType}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
      />
      <PersonaBoxWrapper>
        <PersonaTitleTextWrapper>
          <PersonaTitleText>{displayType(selectedType)}を選択</PersonaTitleText>
        </PersonaTitleTextWrapper>
        <PersonaBox>
          {selectedType === 'businessTypes' && (
            <PersonaInputForm
              items={masterData.businessTypes || []}
              checkedArray={checkedBusinessTypes}
              selectedType={selectedType}
              setCheckedArray={setCheckedBusinessTypes}
            />
          )}
          {selectedType === 'numberOfEmployees' && (
            <PersonaInputForm
              items={masterData.numberOfEmployees || []}
              checkedArray={checkedNumberOfEmployees}
              selectedType={selectedType}
              setCheckedArray={setCheckedNumberOfEmployees}
            />
          )}
          {selectedType === 'needs' && (
            <PersonaNeedsInputForm
              items={masterData.needs || []}
              checkedArray={checkedNeeds}
              selectedType={selectedType}
              setCheckedArray={setCheckedNeeds}
            />
          )}
          {selectedType === 'solutions' && pageType === 'need' && (
            <PersonaSolutionsInputForm
              items={masterData.solutions || []}
              checkedArray={checkedSolutions}
              selectedType={selectedType}
              setCheckedArray={setCheckedSolutions}
            />
          )}
          {selectedType === 'prefectures' && (
            <Prefectures
              masterData={masterData}
              checkedPrefectures={checkedPrefectures}
              setCheckedPrefectures={setCheckedPrefectures}
            />
          )}
          {selectedType === 'positions' && (
            <PersonaInputForm
              items={masterData.positionNumberOptions || []}
              checkedArray={checkedPositionNumberOptions}
              selectedType={selectedType}
              setCheckedArray={setCheckedPositionNumberOptions}
            />
          )}
          {selectedType === 'capitals' && (
            <PersonaInputForm
              items={masterData.capitalNumberOptions || []}
              checkedArray={checkedCapitalNumberOptions}
              selectedType={selectedType}
              setCheckedArray={setCheckedCapitalNumberOptions}
            />
          )}
          {selectedType === 'others' && (
            <>
              {/* NOTE: ペルソナページのみ */}
              {pageType === 'persona' && (
                <SearchDetail
                  handleOtherDetailsChecked={handleOtherDetailsChecked}
                  setOtherDetails={setOtherDetails}
                  persona={persona}
                  setPersona={setPersona}
                />
              )}
              {/* NOTE: ペルソナ・検索ページ共通 */}
              <SearchOther
                masterData={masterData}
                persona={persona}
                setPersona={setPersona}
                checkedFoundedAts={checkedFoundedAts}
                setCheckedFoundedAts={setCheckedFoundedAts}
                checkedBusinessObjects={checkedBusinessObjects}
                setCheckedBusinessObject={setCheckedBusinessObject}
                checkedAdminAges={checkedAdminAges}
                setCheckedAdminAges={setCheckedAdminAges}
              />
              {/* NOTE: 検索ページ && 紹介担当のみ */}
              {pageType === 'need' && role === ROLE.ONLYSTORY_REFERRER && (
                <SearchForReferrer
                  minCreatedDate={minCreatedDate}
                  setMinCreatedDate={setMinCreatedDate}
                  maxCreatedDate={maxCreatedDate}
                  setMaxCreatedDate={setMaxCreatedDate}
                  minLastSignInAt={minLastSignInAt}
                  setMinLastSignInAt={setMinLastSignInAt}
                  maxLastSignInAt={maxLastSignInAt}
                  setMaxLastSignInAt={setMaxLastSignInAt}
                  masterDataRoleForAnalysis={masterDataRoleForAnalysis}
                  checkedRoleForAnalysis={checkedRoleForAnalysis}
                  setCheckedRoleForAnalysis={setCheckedRoleForAnalysis}
                />
              )}
            </>
          )}
        </PersonaBox>
      </PersonaBoxWrapper>
    </>
  )
}

const Prefectures: VFC<{
  masterData: MasterDataStore
  checkedPrefectures: MasterDataWithRegionalDivisionType[]
  setCheckedPrefectures: Dispatch<
    SetStateAction<MasterDataWithRegionalDivisionType[]>
  >
}> = ({ masterData, checkedPrefectures, setCheckedPrefectures }) => {
  const selectAllPrefectures = () => {
    setCheckedPrefectures(masterData.prefectures || [])
  }
  const deselectAllPrefectures = () => {
    setCheckedPrefectures([])
  }
  const {
    hokkaidoPrefectures,
    tohokuPrefectures,
    kantoPrefectures,
    chubuPrefectures,
    kinkiPrefectures,
    chugokuPrefectures,
    shikokuPrefectures,
    kyushuPrefectures,
    kaigai,
  } = categorizePrefecturesByRegion(masterData.prefectures || [])

  return (
    <>
      <Control>
        <button type="button" onClick={() => selectAllPrefectures()}>
          すべて選択
        </button>
        <DeselectButton type="button" onClick={() => deselectAllPrefectures()}>
          解除
        </DeselectButton>
      </Control>
      <Spacer />
      <PrefectureInputForm
        prefectures={hokkaidoPrefectures}
        checkedArray={checkedPrefectures}
        name="北海道"
        id="hokkaido"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={tohokuPrefectures}
        checkedArray={checkedPrefectures}
        name="東北"
        id="tohoku"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={kantoPrefectures}
        checkedArray={checkedPrefectures}
        name="関東"
        id="kanto"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={chubuPrefectures}
        checkedArray={checkedPrefectures}
        name="中部"
        id="chubu"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={kinkiPrefectures}
        checkedArray={checkedPrefectures}
        name="近畿"
        id="kinki"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={chugokuPrefectures}
        checkedArray={checkedPrefectures}
        name="中国"
        id="chugoku"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={shikokuPrefectures}
        checkedArray={checkedPrefectures}
        name="四国"
        id="shikoku"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={kyushuPrefectures}
        checkedArray={checkedPrefectures}
        name="九州"
        id="kyushu"
        setCheckedArray={setCheckedPrefectures}
      />
      <PrefectureInputForm
        prefectures={kaigai}
        checkedArray={checkedPrefectures}
        name="海外"
        id="kaigai"
        setCheckedArray={setCheckedPrefectures}
      />
    </>
  )
}

const SearchForReferrer: VFC<SearchForReferrerProps> = ({
  minCreatedDate,
  setMinCreatedDate,
  maxCreatedDate,
  setMaxCreatedDate,
  minLastSignInAt,
  setMinLastSignInAt,
  maxLastSignInAt,
  setMaxLastSignInAt,
  masterDataRoleForAnalysis,
  checkedRoleForAnalysis,
  setCheckedRoleForAnalysis,
}) => {
  return (
    <>
      <Spacer />
      {setMinCreatedDate && setMaxCreatedDate && (
        <PersonaInputDateForm
          title="登録日"
          start={minCreatedDate || ''}
          end={maxCreatedDate || ''}
          setStart={setMinCreatedDate}
          setEnd={setMaxCreatedDate}
        />
      )}
      <Spacer />
      {setMinLastSignInAt && setMaxLastSignInAt && (
        <PersonaInputDateForm
          title="ログイン日時"
          start={minLastSignInAt || ''}
          end={maxLastSignInAt || ''}
          setStart={setMinLastSignInAt}
          setEnd={setMaxLastSignInAt}
        />
      )}
      <Spacer />
      {setCheckedRoleForAnalysis && (
        <PersonaInputForm
          items={masterDataRoleForAnalysis || []}
          checkedArray={checkedRoleForAnalysis || []}
          selectedType={'roleForAnalysis'}
          setCheckedArray={setCheckedRoleForAnalysis}
          isNested={true}
          nestedName="会員プラン"
        />
      )}
    </>
  )
}

const SearchDetail: VFC<SearchDetailProps> = ({
  handleOtherDetailsChecked,
  setOtherDetails,
  persona,
  setPersona,
}) => {
  return (
    <>
      <NestedChecklabelHeader>
        <CheckboxDiv color={orange}>
          <input
            id="other_details"
            type="checkbox"
            onChange={({ target: { checked } }) =>
              setOtherDetails && setOtherDetails(checked)
            }
            checked={handleOtherDetailsChecked}
          />
          <label htmlFor="other_details">その他詳細条件</label>
        </CheckboxDiv>
      </NestedChecklabelHeader>
      <SelectableItems>
        <SelectableItemDiv>
          <input
            id="is_new"
            type="checkbox"
            onChange={({ target: { checked } }) =>
              setPersona({ ...persona, isNew: checked })
            }
            checked={persona.isNew}
          />
          <label htmlFor="is_new">新着企業のみ</label>
        </SelectableItemDiv>
        <SelectableItemDiv>
          <input
            id="no_message"
            type="checkbox"
            onChange={({ target: { checked } }) =>
              setPersona({ ...persona, noMessage: checked })
            }
            checked={persona.noMessage}
          />
          <label htmlFor="no_message">メッセージ送信済み会員を除外</label>
        </SelectableItemDiv>
        <SelectableItemDiv>
          <input
            id="refuse_message"
            type="checkbox"
            onChange={({ target: { checked } }) =>
              setPersona({ ...persona, refuseMessage: checked })
            }
            checked={persona.refuseMessage}
          />
          <label htmlFor="refuse_message">メッセージ受信拒否会員を除外</label>
        </SelectableItemDiv>
      </SelectableItems>
      <Spacer />
    </>
  )
}

const SearchOther: VFC<SearchOtherProps> = ({
  masterData,
  persona,
  setPersona,
  checkedFoundedAts,
  setCheckedFoundedAts,
  checkedBusinessObjects,
  setCheckedBusinessObject,
  checkedAdminAges,
  setCheckedAdminAges,
}) => {
  return (
    <>
      <NestedChecklabelHeader>
        <label>上場・未上場</label>
      </NestedChecklabelHeader>
      <SelectableItems>
        <SelectableItemDiv>
          <input
            id="is_listed_company"
            type="checkbox"
            onChange={({ target: { checked } }) =>
              // NOTE: checkedを外す + 上場を選択済みの場合はnullを代入する
              !checked && persona.isListedCompany
                ? setPersona({
                    ...persona,
                    isListedCompany: null,
                  })
                : setPersona({
                    ...persona,
                    isListedCompany: true,
                  })
            }
            // NOTE: 上場・未上場を選択しない(null)の状態を許容するため明示的に書く
            checked={persona.isListedCompany === true}
          />
          <label htmlFor="is_listed_company">上場</label>
        </SelectableItemDiv>
        <SelectableItemDiv>
          <input
            id="is_unlisted_company"
            type="checkbox"
            // NOTE: checkedを外す + 未上場を選択済みの場合はnullを代入する
            onChange={({ target: { checked } }) =>
              !checked && !persona.isListedCompany
                ? setPersona({
                    ...persona,
                    isListedCompany: null,
                  })
                : setPersona({
                    ...persona,
                    isListedCompany: false,
                  })
            }
            // NOTE: 上場・未上場を選択しない(null)の状態を許容するため明示的に書く
            checked={persona.isListedCompany === false}
          />
          <label htmlFor="is_unlisted_company">未上場</label>
        </SelectableItemDiv>
      </SelectableItems>
      <Spacer />
      <PersonaInputForm
        items={masterData.foundedAts || []}
        checkedArray={checkedFoundedAts}
        selectedType={'foundedAts'}
        setCheckedArray={setCheckedFoundedAts}
        isNested={true}
        nestedName="設立年数"
      />
      <Spacer />
      <PersonaInputForm
        items={masterData.businessObjects || []}
        checkedArray={checkedBusinessObjects}
        selectedType={'businessObjects'}
        setCheckedArray={setCheckedBusinessObject}
        isNested={true}
        nestedName="商材"
      />
      <Spacer />
      <PersonaInputForm
        items={masterData.adminAges || []}
        checkedArray={checkedAdminAges}
        selectedType={'adminAges'}
        setCheckedArray={setCheckedAdminAges}
        isNested={true}
        nestedName="決裁者の年齢"
      />
    </>
  )
}

const PersonaBoxWrapper = styled.div`
  width: 100%;
`

const PersonaBox = styled.div`
  margin-left: 24px;
  height: 380px;
  overflow-y: scroll;
`

const PersonaTitleTextWrapper = styled.div`
  width: 100%;
  height: 65px;
  border-top-right-radius: 14px;
  display: flex;
  align-items: center;
`

const PersonaTitleText = styled.p`
  font-size: 20px;
  color: #a8a9ac;
  margin-top: -4px;
  margin-left: 24px;
`

const PersonaNav = styled.nav`
  min-width: 200px;
  font-size: 15px;
  font-weight: ${fontWeightBold};
  color: #3b3947;
  cursor: pointer;
`

const PersonaMenu = styled.ul`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`

const PersonaMenuList = styled.li<{ open?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0px 3px 6px #00000029;
  height: 100%;
  :first-child {
    border-top-left-radius: 14px;
  }
  :last-child {
    border-bottom-left-radius: 14px;
  }
  ${({ open }) =>
    open &&
    css`
      color: ${truewhite};
      background: ${orange};
      transition: 0.2s;
    `};
`

const PersonaMenuListText = styled.p`
  margin: 0;
`

export const FormSpacer = styled.hr<{
  marginTop?: number
  marginBottom?: number
}>`
  margin-top: ${({ marginTop }) => (marginTop ? `${marginTop}px` : '19px')};
  margin-bottom: ${({ marginBottom }) =>
    marginBottom ? `${marginBottom}px` : '19px'};
  border: 0;
  border-top: 1px solid ${_profilehrborder};
`

export const Control = styled.div`
  display: flex;
  font-size: 14px;
  font-weight: ${fontWeightBold};
  color: ${mediumGray};
  padding-bottom: 12px;

  button {
    display: block;
    padding: 0 12px;
    border-left: 1px solid ${bordergray};
    :first-child {
      padding: 0 12px 0 0;
      border-left: none;
    }
  }
`

export const DeselectButton = styled.button`
  color: ${orange};
`

const Spacer = styled.div`
  margin-bottom: 10px;
`
